import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  Autocomplete,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import AddIcon from '@mui/icons-material/Add';
import { toggleTutEditMode } from '../../../../redux/slices/tutorialEditSlice';
import AddVideo from './AddVideo';
import EditVideo from './EditVideo';
import MachineListAPIs from '../../../../utilities/api/machine/AdminMachineList';
import EquipmentListAPIs from '../../../../utilities/api/equipment/AdminEquipmentList';
import InventoryListAPIs from '../../../../utilities/api/inventory/AdminInventoryList';
import AdminModuleAPIs from '../../../../utilities/api/tutorials/AdminModuleAPIs';
import AdminVideoAPIs from '../../../../utilities/api/tutorials/AdminVideoAPIs';
import { ImageBaseURL } from '../../../../utilities/api/axios';

const EditModuleForm = () => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.user.user);
  const currentItem = useSelector(state => state.tutorialEdit.tutCurrentItem);

  const [videoComponents, setVideoComponents] = useState([]);
  const [existingVideos, setExistingVideos] = useState([]);
  const [videoUrls, setVideoUrls] = useState([]);
  const [videoIds, setVideoIds] = useState([]);
  const [isFormFilled, setIsFormFilled] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const [machines, setMachines] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [inventories, setInventories] = useState([]);
  const [thumbnail, setThumbnail] = useState(null);


  const validationSchema = Yup.object({
    name: Yup.string().required('Module Name is required'),
    description: Yup.string().required('Module Description is required'),
  });

  const formik = useFormik({
    initialValues: {
      name: currentItem?.name || '',
      description: currentItem?.description || '',
      related_machines: [],
      related_equipments: [],
      related_inventory: [],
      image: currentItem?.image || null,
    },
    enableReinitialize: true, // Allow Formik to update when initialValues change
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('name', values.name);
        formData.append('description', values.description);
        
        // Add related fields
        values.related_machines.forEach(machine => formData.append('related_machines', machine.id));
        values.related_equipments.forEach(equipment => formData.append('related_equipments', equipment.id));
        values.related_inventory.forEach(inventory => formData.append('related_inventory', inventory.id));

        // Add videos as JSON string
        const videoPayload = videoIds.map((video, index) => ({
          video: video.id,
          order: index + 1,
        }));
        formData.append('videos', JSON.stringify(videoPayload));

        // Add thumbnail if selected
        if (thumbnail) {
          formData.append('image', thumbnail);
        }

        // Single PATCH request for everything
        await AdminModuleAPIs.UpdateModule(currentItem.id, formData);
        console.log('Module updated successfully');

        // Update existing video details if changed
        for (const video of existingVideos) {
          await AdminVideoAPIs.UpdateVideo(video.id, {
            title: video.title,
            description: video.description,
            link: video.link,
          });
        }

        setSnackbar({ open: true, message: 'Module updated successfully!', severity: 'success' });
        // Optional: close edit mode after a short delay
        setTimeout(() => dispatch(toggleTutEditMode()), 1500);
      } catch (error) {
        console.error('Error updating module:', error);
        setSnackbar({ open: true, message: 'Failed to update module. Please try again.', severity: 'error' });
      } finally {
        setUploading(false);
      }
    },
  });

  useEffect(() => {
    const { name, description } = formik.values;
    const formFilled = name && description;
    setIsFormFilled(formFilled);
  }, [formik.values]);

  // Load options and module details
  useEffect(() => {
    const loadData = async () => {
      if (!currentItem?.id) return;

      try {
        // Fetch dropdown options and module details in parallel
        const [machinesData, equipmentsData, inventoriesData, fullData] = await Promise.all([
          MachineListAPIs.MachineListGet(),
          EquipmentListAPIs.EquipmentListGet(),
          InventoryListAPIs.InventoryListGet({
            refresh_token: user.refresh,
            access_token: user.access,
          }),
          AdminModuleAPIs.ModuleDetailGet(currentItem.id)
        ]);

        const mList = machinesData || [];
        const eList = equipmentsData || [];
        const iList = inventoriesData || [];

        setMachines(mList);
        setEquipments(eList);
        setInventories(iList);

        // Populate videos with full details
        if (fullData.videos) {
          const videoDetails = await Promise.all(
            fullData.videos.map(async item => {
              try {
                const videoResp = await AdminVideoAPIs.VideoDetailGet(item.video);
                return {
                  id: videoResp.id,
                  title: videoResp.title || '',
                  description: videoResp.description || '',
                  link: videoResp.link || '',
                  order: item.order,
                };
              } catch (err) {
                console.error('Error fetching video details:', err);
                return null;
              }
            })
          );
          const filteredVideos = videoDetails.filter(v => v !== null);
          setExistingVideos(filteredVideos);
          setVideoIds(filteredVideos);
        }

        // Update Formik values with everything fetched
        formik.setValues({
          name: fullData.name || '',
          description: fullData.description || '',
          related_machines: mList.filter(m => fullData.related_machines?.includes(m.id)),
          related_equipments: eList.filter(e => fullData.related_equipments?.includes(e.id)),
          related_inventory: iList.filter(i => fullData.related_inventory?.includes(i.id)),
          image: fullData.image || null,
        });

      } catch (error) {
        console.error('Failed to load module data', error);
      }
    };

    if (user) loadData();
  }, [user.refresh, user.access, currentItem?.id]);

  const handleCancel = () => {
    const userConfirmed = window.confirm(
      'All data will be lost. Are you sure you want to cancel?'
    );
    if (userConfirmed) {
      dispatch(toggleTutEditMode());
    }
  };

  const addVideoComponent = () => {
    setVideoComponents([...videoComponents, { id: Date.now() }]);
  };

  const removeVideoComponent = id => {
    setVideoComponents(videoComponents.filter(video => video.id !== id));
  };

  const handleUploadSuccess = (videoId, videoUrl) => {
    const newVideo = { id: videoId, link: videoUrl, order: videoIds.length + 1 };
    setVideoIds([...videoIds, newVideo]);
    setVideoUrls([...videoUrls, videoUrl]);
  };

  const handleUploading = isUploading => {
    setUploading(isUploading);
  };

  const handleVideoChange = (id, field, value) => {
    setExistingVideos(prevVideos =>
      prevVideos.map(video =>
        video.id === id ? { ...video, [field]: value } : video
      )
    );
    setVideoIds(prevVideoIds =>
      prevVideoIds.map(video =>
        video.id === id ? { ...video, [field]: value } : video
      )
    );
  };

  const removeExistingVideoComponent = id => {
    setExistingVideos(existingVideos.filter(video => video.id !== id));
    setVideoIds(videoIds.filter(video => video.id !== id));
  };

  const handleImageChange = event => {
    const file = event.target.files[0];
    setThumbnail(file);
  };

  return (
    <Box
      component="form"
      onSubmit={formik.handleSubmit}
      encType="multipart/form-data"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '95%',
        margin: '0 auto',
        padding: 2,
        position: 'relative',
      }}
    >
      <Typography variant="h5" sx={{ mb: 1 }}>
        Edit Module
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography>Module Name</Typography>
        <TextField
          label="Module Name"
          name="name"
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          fullWidth
          required
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
        />
        <Typography>Module Description</Typography>
        <TextField
          label="Module Description"
          name="description"
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          fullWidth
          required
          multiline
          rows={4}
          error={
            formik.touched.description && Boolean(formik.errors.description)
          }
          helperText={formik.touched.description && formik.errors.description}
        />
        <Typography>Module Thumbnail</Typography>
        {currentItem?.image && (
          <Box sx={{ mb: 2 }}>
            <img
              // src={currentItem.image}
              src={currentItem.image.startsWith('http') ? currentItem.image : `${ImageBaseURL}${currentItem.image}`}
              alt={currentItem.image.split('/').pop()}
              style={{ width: '250px', height: '250px', objectFit: 'cover' }}
            />
          </Box>
        )}
        <input
          accept="image/*"
          id="thumbnail"
          type="file"
          onChange={handleImageChange}
        />
        {formik.errors.image && (
          <Typography color="error">{formik.errors.image}</Typography>
        )}
        {machines.length > 0 && (
          <>
            <Typography>Related Machines</Typography>
            <Autocomplete
              multiple
              options={machines}
              getOptionLabel={(option) => option?.name || ''}
              value={formik.values.related_machines}
              onChange={(event, value) =>
                formik.setFieldValue('related_machines', value)
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Related Machines"
                  name="related_machines"
                  error={
                    formik.touched.related_machines &&
                    Boolean(formik.errors.related_machines)
                  }
                  helperText={
                    formik.touched.related_machines &&
                    formik.errors.related_machines
                  }
                />
              )}
              disableCloseOnSelect
            />
          </>
        )}
        {equipments.length > 0 && (
          <>
            <Typography>Related Equipments</Typography>
            <Autocomplete
              multiple
              options={equipments}
              getOptionLabel={(option) => option?.name || ''}
              value={formik.values.related_equipments}
              onChange={(event, value) =>
                formik.setFieldValue('related_equipments', value)
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Related Equipments"
                  name="related_equipments"
                  error={
                    formik.touched.related_equipments &&
                    Boolean(formik.errors.related_equipments)
                  }
                  helperText={
                    formik.touched.related_equipments &&
                    formik.errors.related_equipments
                  }
                />
              )}
              disableCloseOnSelect
            />
          </>
        )}
        {inventories.length > 0 && (
          <>
            <Typography>Related Inventory</Typography>
            <Autocomplete
              multiple
              options={inventories}
              getOptionLabel={(option) => option?.name || ''}
              value={formik.values.related_inventory}
              onChange={(event, value) =>
                formik.setFieldValue('related_inventory', value)
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Related Inventory"
                  name="related_inventory"
                  error={
                    formik.touched.related_inventory &&
                    Boolean(formik.errors.related_inventory)
                  }
                  helperText={
                    formik.touched.related_inventory &&
                    formik.errors.related_inventory
                  }
                />
              )}
              disableCloseOnSelect
            />
          </>
        )}
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Module Videos</Typography>
        <Box>
          {existingVideos.map(video => (
            <EditVideo
              key={video.id}
              video={video}
              onRemove={() => removeExistingVideoComponent(video.id)}
              onChange={(id, field, value) =>
                handleVideoChange(id, field, value)
              }
            />
          ))}
          {videoComponents.map(video => (
            <AddVideo
              key={video.id}
              onRemove={() => removeVideoComponent(video.id)}
              onUploadSuccess={handleUploadSuccess}
              isFormFilled={isFormFilled}
              handleUploading={handleUploading}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={addVideoComponent}
          >
            <AddIcon sx={{ marginRight: '2px', padding: '0px' }} />
            Add Video
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 2 }}>
        <Button variant="outlined" color="secondary" onClick={handleCancel} disabled={uploading}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={uploading}
          startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {uploading ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditModuleForm;
